import React, {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useContext,
  useMemo,
} from 'react';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import DisplayRichTextString from 'components/RichTextEditorField/components/DisplayRichTextString';
import { getUserFullName } from 'utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import RadioGroupField from 'components/RadioGroupField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PencilIcon from 'icons/Pencil.icon';
import FaqContext from '../../providers/Faq/Faq.context';
import { FAQAccess } from 'router/subrouters/DataRoom/components/FAQAccess';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useHeightAnimation from 'hooks/useHeightAnimation';
import {
  getFAQAccess,
  processFAQAccess,
} from 'router/subrouters/DataRoom/components/FAQAccess/utils';
import QuestionFiles from 'router/subrouters/DataRoom/components/QuestionFiles';
import AnswerFiles from 'router/subrouters/DataRoom/components/AnswerFiles';
import ManageFaqText from 'router/subrouters/DataRoom/components/AddQuestionToFaq/components/ManageFaqText';

import './ManageFaqDrawer.styles.scss';
import './ManageFaqDrawer.styles.responsive.scss';

type ManageFaqDrawerProps = {
  className?: string;
  onClose?: () => void;
  question: Question;
  onQuestionsUpdate?: (q: Question) => void;
};

const MANAGE_OPTIONS = {
  EDIT: 'edit',
  PUBLISH: 'publish',
};

const ManageFaqDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageFaqDrawerProps
> = ({ className, question, onQuestionsUpdate, onClose }, ref) => {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const { t } = useTranslation();
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { openForEdit, setQuestionsLoading, faqAccessData } =
    useContext(FaqContext);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const classes = classNames('manage-faq-drawer', className);

  useImperativeHandle(ref, () => drawer, [drawer]);

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  const handleOnEdit = useCallback(() => {
    drawer.setOpen(false);
    openForEdit({
      ...question,
      isPublishedToFAQ: true,
    });
  }, [drawer, openForEdit, question]);

  const onManageSubmit = useCallback(
    async (values) => {
      if (values.editAndPublishToFaq === MANAGE_OPTIONS.EDIT) {
        handleOnEdit();

        return;
      } else {
        const isPublishedToFAQ =
          values.editAndPublishToFaq === MANAGE_OPTIONS.PUBLISH
            ? true
            : values.isPublishedToFAQ;

        const newValues = processFAQAccess(values, faqAccessData);

        try {
          setQuestionsLoading(true);

          const { data } = await dataRoomApi.faqs.updateFaq(
            question._id,
            dataRoomId,
            { ...values, ...newValues, isPublishedToFAQ },
          );

          drawer.setOpen(false);
          onQuestionsUpdate?.(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setQuestionsLoading(false);
        }
      }
    },
    [
      dataRoomId,
      drawer,
      faqAccessData,
      handleOnEdit,
      onQuestionsUpdate,
      popServerError,
      question._id,
      setQuestionsLoading,
    ],
  );

  const initialValues = useMemo(
    () => ({
      isPublishedToFAQ: question.isPublishedToFAQ,
      ...getFAQAccess(question),
    }),
    [question],
  );

  return (
    <SideDrawer className={classes} ref={drawerRef} closeAction={onClose}>
      <p className="drawer-title">{t('manageThisAnswer')}</p>
      <div className="manage-faq-drawer__question">
        <span className="manage-faq-drawer__question__subject">
          <p>{question.subject}</p>

          <Button icon={PencilIcon} variant="link" onClick={handleOnEdit}>
            {t('editThisQuestion')}
          </Button>
        </span>
        <span className="manage-faq-drawer__question__author">
          {!question.isFromQA
            ? t('providedByAdmin')
            : `${t('by')} ${getUserFullName(
                question.user?.firstName,
                question.user?.lastName,
              )}`}
        </span>

        <DisplayRichTextString
          modalTitle={question.subject}
          key={question.content}
        >
          {question.content}
        </DisplayRichTextString>

        <QuestionFiles
          questionId={question._id}
          files={question.files}
          updateQuestion={(files) => onQuestionsUpdate({ ...question, files })}
        />
      </div>

      {question.dataRoomAnswer?.content && (
        <div className="manage-faq-drawer__answer">
          {question.isFromQA && (
            <span className="manage-faq-drawer__answer__author">
              <p>
                {`${t('manageAnswerSellOptions.answered')} ${t('by')} ${
                  getUserFullName(
                    question.dataRoomAnswer.user?.firstName,
                    question.dataRoomAnswer.user?.lastName,
                  ) || t('unknown')
                }`}
              </p>
            </span>
          )}

          <DisplayRichTextString
            modalTitle={t('manageAnswerSellOptions.answer')}
            key={question.dataRoomAnswer?.content}
          >
            {question.dataRoomAnswer?.content}
          </DisplayRichTextString>

          <AnswerFiles
            answerId={question.dataRoomAnswer._id}
            files={question.dataRoomAnswer?.files}
            updateAnswer={(files) =>
              onQuestionsUpdate({
                ...question,
                dataRoomAnswer: { ...question.dataRoomAnswer, files },
              })
            }
          />
        </div>
      )}

      <ManageFaqText />

      <Form
        onSubmit={onManageSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, invalid, values }) => {
          const editAndPublishToFaq = values.editAndPublishToFaq;
          const isPublishedToFAQ = values.isPublishedToFAQ;

          return (
            <form onSubmit={handleSubmit}>
              <div className="manage-faq-drawer__publish">
                {!question.isPublishedToFAQ ? (
                  <RadioGroupField
                    name="editAndPublishToFaq"
                    options={[
                      {
                        value: MANAGE_OPTIONS.PUBLISH,
                        label: t('publishThisToFaq'),
                      },
                      {
                        value: MANAGE_OPTIONS.EDIT,
                        label: t('editFaqQuestionBeforePublished'),
                      },
                    ]}
                  />
                ) : (
                  <Field
                    name="isPublishedToFAQ"
                    label={t('publishThisToFaq')}
                    type="checkbox"
                    component={CheckboxField}
                  />
                )}
              </div>

              <div ref={containerRef} className="animation-container">
                {(editAndPublishToFaq === MANAGE_OPTIONS.PUBLISH ||
                  isPublishedToFAQ === true) && (
                  <FAQAccess showFAQAccess data={faqAccessData} />
                )}
              </div>

              <div className="manage-faq-drawer__actions">
                <Button type="submit" disabled={submitting || invalid}>
                  {t('saveAnswerStatus')}
                </Button>
              </div>
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageFaqDrawer);
